import axios from 'axios';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Products for a set of SKUs, with every Market catalog's price for each.
 *
 * One request covers the catalog list and all catalog prices, so changing location or
 * catalog is a local lookup rather than a refetch.
 *
 * @param {Object} args
 * @param {boolean} args.enabled
 * @param {Array<string>} args.skus
 */
export function useLabelPricesQuery({ enabled, skus }) {
    // Sorted and deduped so the query key is stable regardless of row order.
    const requestedSkus = useMemo(() => [...new Set((skus || []).filter(Boolean))].sort(), [skus]);

    const query = useQuery({
        queryKey: ['label-prices', requestedSkus],
        queryFn: async () => {
            const response = await axios.post(`${API_URL}/api/app/products/search`, { skus: requestedSkus }, { withCredentials: true });
            return response?.data?.data ?? { catalogs: [], variants: [] };
        },
        enabled: Boolean(enabled) && requestedSkus.length > 0,
        staleTime: 5 * 60 * 1000, // 5 minutes, matching the server-side price cache
    });

    const variants = query.data?.variants ?? [];

    const pricesBySku = useMemo(() => {
        const bySku = new Map();
        variants.forEach((variant) => {
            if (variant?.sku) bySku.set(variant.sku, variant);
        });
        return bySku;
    }, [variants]);

    return {
        ...query,
        catalogs: query.data?.catalogs ?? [],
        variants,
        pricesBySku,
    };
}
